import { SaveMessageAction } from './SaveMessageAction'
import { canSaveMessage } from '@/lib/savedMessages'
import { Copy, Flag, Forward, Hash, Link, ListChecks, MessageSquarePlus, Pencil, Pin, PinOff, Reply, SmilePlus, TextSelect, Trash2 } from 'lucide-react'
import { ContextMenu, MenuCopy, MenuItem, MenuSeparator } from '../ContextMenu'
import { channelLink } from '@/lib/appLinks'
import { useAppStore } from '@/store/app'
import type { Message } from '@/types/api'

interface Props {
  message: Message
  /** Сервер канала; без него — ссылка на личные сообщения (@me). */
  guildId?: string | undefined
  channelId: string
  x: number
  y: number
  align?: 'right' | undefined
  onClose: () => void
  onReact?: (() => void) | undefined
  onReply: () => void
  onForward?: (() => void) | undefined
  onThread?: (() => void) | undefined
  onEdit?: (() => void) | undefined
  onPin?: (() => void) | undefined
  onReport?: (() => void) | undefined
  onDelete?: (() => void) | undefined
  onSelectForDeletion?: (() => void) | undefined
  onSelectText?: (() => void) | undefined
}

/**
 * Меню сообщения — по удержанию, правой кнопке и по «…»: те же действия, что в панели,
 * плюс копирование текста и ссылки на сообщение.
 */
export function MessageMenu({ message, guildId, channelId, x, y, align, onClose, onReact, onReply, onForward, onThread, onEdit, onPin, onReport, onDelete, onSelectForDeletion, onSelectText }: Props) {
  const devMode = useAppStore((s) => s.devMode)
  const run = (fn: () => void) => () => {
    onClose()
    fn()
  }
  return (
    <ContextMenu x={x} y={y} align={align ?? 'left'} label="Действия с сообщением" onClose={onClose}>
      {onReact ? <MenuItem icon={<SmilePlus size={16} />} label="Добавить реакцию" onClick={run(onReact)} /> : null}
      <MenuItem icon={<Reply size={16} />} label="Ответить" onClick={run(onReply)} />
      {onForward ? <MenuItem icon={<Forward size={16} />} label="Переслать" onClick={run(onForward)} /> : null}
      {canSaveMessage(message) ? <SaveMessageAction channelId={channelId} messageId={message.id} onClose={onClose} /> : null}
      {onThread ? <MenuItem icon={<MessageSquarePlus size={16} />} label="Создать ветку" onClick={run(onThread)} /> : null}
      {onEdit ? <MenuItem icon={<Pencil size={16} />} label="Изменить" onClick={run(onEdit)} /> : null}
      {onPin ? <MenuItem icon={message.pinned ? <PinOff size={16} /> : <Pin size={16} />} label={message.pinned ? 'Открепить' : 'Закрепить'} onClick={run(onPin)} /> : null}
      <MenuSeparator />
      {onSelectText ? <MenuItem icon={<TextSelect size={16} />} label="Выделить текст" onClick={run(onSelectText)} /> : null}
      {message.content ? <MenuCopy icon={<Copy size={16} />} label="Скопировать текст" done="Текст скопирован" text={message.content} onDone={onClose} /> : null}
      <MenuCopy icon={<Link size={16} />} label="Скопировать ссылку на сообщение" done="Ссылка скопирована" text={channelLink(location.origin, guildId ?? null, channelId, message.id)} onDone={onClose} />
      {devMode ? <MenuCopy icon={<Hash size={16} />} label="Скопировать ID" done="ID скопирован" text={message.id} onDone={onClose} /> : null}
      {onReport || onDelete ? <MenuSeparator /> : null}
      {onReport ? <MenuItem icon={<Flag size={16} />} label="Пожаловаться" onClick={run(onReport)} /> : null}
      {onDelete ? <MenuItem icon={<Trash2 size={16} />} label="Удалить" danger onClick={run(onDelete)} /> : null}
      {onSelectForDeletion ? <MenuItem icon={<ListChecks size={16} />} label="Выбрать для удаления" onClick={run(onSelectForDeletion)} /> : null}
    </ContextMenu>
  )
}
